/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Migrate the mParticle Braze kit", description = "Replace mParticle's Braze kit with a Hightouch plugin while preserving user identities, events, purchases, and in-app behavior.", lastUpdatedDate = new Date(1791763200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Migrate the mParticle Braze kit",
        description: "Replace mParticle's Braze kit with a Hightouch plugin while preserving user identities, events, purchases, and in-app behavior.",
        lastUpdatedDate: "Mon Oct 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Plan the replacement",
          "depth": 0,
          "id": "plan-the-replacement"
        }, {
          "value": "Install the replacement plugin",
          "depth": 0,
          "id": "install-the-replacement-plugin"
        }, {
          "value": "Preserve identity and user attributes",
          "depth": 0,
          "id": "preserve-identity-and-user-attributes"
        }, {
          "value": "Match kit settings and event names",
          "depth": 0,
          "id": "match-kit-settings-and-event-names"
        }, {
          "value": "Preserve purchase output",
          "depth": 0,
          "id": "preserve-purchase-output"
        }, {
          "value": "Browser purchases",
          "depth": 1,
          "id": "browser-purchases"
        }, {
          "value": "iOS purchases",
          "depth": 1,
          "id": "ios-purchases"
        }, {
          "value": "Android purchases",
          "depth": 1,
          "id": "android-purchases"
        }, {
          "value": "React Native purchases",
          "depth": 1,
          "id": "react-native-purchases"
        }, {
          "value": "Preserve bundled mobile purchases",
          "depth": 1,
          "id": "preserve-bundled-mobile-purchases"
        }, {
          "value": "Preserve messaging and application behavior",
          "depth": 0,
          "id": "preserve-messaging-and-application-behavior"
        }, {
          "value": "Validate and cut over",
          "depth": 0,
          "id": "validate-and-cut-over"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      a: "a",
      pre: "pre",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Use this guide when replacing an mParticle Braze kit in a browser, iOS, Android, or React Native application. Configure the Hightouch Braze plugin to preserve the data your Braze campaigns and segments depend on."
      }), "\n", _jsx(_components.h2, {
        id: "plan-the-replacement",
        children: "Plan the replacement"
      }), "\n", _jsx(_components.p, {
        children: "The plugin forwards Hightouch calls to the Braze SDK in your application. Your Hightouch SDK also sends events to Hightouch for collection. Braze message presentation, Content Cards, and push configuration remain part of your application setup."
      }), "\n", _jsx(_components.p, {
        children: "The plugin uses Hightouch event names and properties by default. mParticle's kits have different mappings, including differences between browser, iOS, and Android. Record your current kit version, settings, and sample Braze output before replacing it."
      }), "\n", _jsx(_components.p, {
        children: "Inventory these settings and behaviors:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The identity selected by ", _jsx(_components.code, {
            children: "userIdentificationType"
          }), ", including its formatting and casing."]
        }), "\n", _jsx(_components.li, {
          children: "Screen or page forwarding, page event names, product identifiers, and bundled commerce events."
        }), "\n", _jsx(_components.li, {
          children: "Attribute types, event-to-attribute rules, subscription groups, and consent handling."
        }), "\n", _jsx(_components.li, {
          children: "In-app message display, Content Cards, push registration, and custom Braze callbacks."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Use separate test profiles or a test Braze app during comparison. Sending the same event through both kits into production can trigger duplicate purchases or messages."
      }), "\n", _jsx(_components.h2, {
        id: "install-the-replacement-plugin",
        children: "Install the replacement plugin"
      }), "\n", _jsxs(_components.p, {
        children: ["Follow the setup for ", _jsx(_components.a, {
          href: "/events/sdks/browser#braze",
          children: "browser"
        }), ", ", _jsx(_components.a, {
          href: "/events/sdks/ios#braze",
          children: "iOS"
        }), ", ", _jsx(_components.a, {
          href: "/events/sdks/android#braze",
          children: "Android"
        }), ", or ", _jsx(_components.a, {
          href: "/events/sdks/react-native#braze",
          children: "React Native"
        }), ". Reuse an existing Braze instance where the SDK supports it, or let the plugin initialize Braze. Avoid initializing Braze twice."]
      }), "\n", _jsxs(_components.p, {
        children: ["Replace mParticle tracking calls with Hightouch calls using the ", _jsx(_components.a, {
          href: "/events/event-spec",
          children: "event specification"
        }), ". Use ", _jsx(_components.code, {
          children: "identify"
        }), " for user IDs and traits, ", _jsx(_components.code, {
          children: "track"
        }), " for custom events and purchases, and ", _jsx(_components.code, {
          children: "page"
        }), " or ", _jsx(_components.code, {
          children: "screen"
        }), " for views."]
      }), "\n", _jsx(_components.h2, {
        id: "preserve-identity-and-user-attributes",
        children: "Preserve identity and user attributes"
      }), "\n", _jsxs(_components.p, {
        children: ["Pass the same identity value that your mParticle kit used as Hightouch's ", _jsx(_components.code, {
          children: "userId"
        }), ". For example, if the kit used Customer ID, continue sending that value. Switching to an email address or mParticle ID can create a different Braze profile."]
      }), "\n", _jsx(_components.p, {
        children: "Rename reserved mParticle attributes at your call site. The following examples use JavaScript, but the trait names apply to all four SDKs:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"customer-123\""
          }), ", {\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "firstName"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Jane\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// Previously $FirstName"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "lastName"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Doe\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// Previously $LastName"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "email"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"jane@example.com\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "phone"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"+14155550123\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// Previously $Mobile"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "birthday"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"1990-01-15\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "address"
          }), ": { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "city"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "country"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"US\""
          }), " },\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "plan"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"premium\""
          }), ",\n});\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The mobile kits map ", _jsx(_components.code, {
          children: "$Zip"
        }), " to a custom Braze attribute named ", _jsx(_components.code, {
          children: "Zip"
        }), "; send a ", _jsx(_components.code, {
          children: "Zip"
        }), " trait to preserve it. If your kit derived a birth date from ", _jsx(_components.code, {
          children: "$Age"
        }), ", preserve that date with ", _jsx(_components.code, {
          children: "birthday"
        }), " rather than sending age as a custom attribute. Check gender values and date formats against a test profile rather than assuming every kit accepted the same input."]
      }), "\n", _jsx(_components.p, {
        children: "Preserve custom attribute types. If your previous output used strings for specific fields, convert those fields explicitly in your instrumentation or purchase transformer. Keep other values in their original types."
      }), "\n", _jsxs(_components.p, {
        children: ["Calling Hightouch's ", _jsx(_components.code, {
          children: "reset()"
        }), " clears Hightouch identity and the plugin's attribute cache. It does not log the current user out of Braze. A later ", _jsx(_components.code, {
          children: "identify"
        }), " with another user ID changes the Braze user."]
      }), "\n", _jsx(_components.h2, {
        id: "match-kit-settings-and-event-names",
        children: "Match kit settings and event names"
      }), "\n", _jsx(_components.p, {
        children: "Apply the settings that match your existing kit. The purchase recipes below show where to configure the platform-specific options."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Existing kit behavior"
            }), _jsx(_components.th, {
              children: "Hightouch configuration"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Forward views"
            }), _jsxs(_components.td, {
              children: ["Use ", _jsx(_components.code, {
                children: "pageTracking: \"name\""
              }), " on browser or ", _jsx(_components.code, {
                children: "forwardScreenViews: true"
              }), " on mobile. Both are off by default."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Browser page names"
            }), _jsxs(_components.td, {
              children: ["Set ", _jsx(_components.code, {
                children: "pageTracking: \"path\""
              }), " to use the URL path. Use ", _jsx(_components.code, {
                children: "\"name\""
              }), " to prefer the page name, falling back to the path."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Browser product names"
            }), _jsxs(_components.td, {
              children: ["Set ", _jsx(_components.code, {
                children: "purchaseGrouping: { mode: \"perProduct\", identifier: \"name\" }"
              }), ". Keep SKU mode if ", _jsx(_components.code, {
                children: "forwardSkuAsProductName"
              }), " was enabled."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Mobile product names"
            }), _jsx(_components.td, {
              children: "Select product names in your platform's purchase grouping options, as described below."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Bundle mobile purchases"
            }), _jsxs(_components.td, {
              children: ["Select per-order purchases and apply the ", _jsx(_components.a, {
                href: "#preserve-bundled-mobile-purchases",
                children: "bundled purchase recipe"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "String values"
            }), _jsx(_components.td, {
              children: "Convert only the fields that previously used strings; preserve the types of other fields."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Browser message display"
            }), _jsxs(_components.td, {
              children: ["Set ", _jsx(_components.code, {
                children: "automaticallyShowInAppMessages: true"
              }), " when the plugin initializes Braze."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "To preserve mobile product-name identifiers, configure the appropriate option:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "iOS:"
          }), " Set ", _jsx(_components.code, {
            children: "purchaseGrouping: .perProduct(identifier: .name)"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Android:"
          }), " Set ", _jsx(_components.code, {
            children: ".purchaseGrouping(PurchaseGrouping.perProduct(ProductIdentifier.NAME))"
          }), ", importing both types from ", _jsx(_components.code, {
            children: "com.hightouch.analytics.braze"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "React Native:"
          }), " Set ", _jsx(_components.code, {
            children: "purchaseGrouping: { mode: \"perProduct\", identifier: \"name\" }"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Custom ", _jsx(_components.code, {
          children: "track"
        }), " events retain the name you send. Other commerce actions, such as ", _jsx(_components.code, {
          children: "Product Added"
        }), ", become custom events. The plugin does not automatically reproduce mParticle's expanded commerce event names (for example, ", _jsx(_components.code, {
          children: "eCommerce - add_to_cart - Item"
        }), ") or split one event into several events. Preserve any names, properties, and extra calls your Braze campaigns require in your instrumentation."]
      }), "\n", _jsx(_components.h2, {
        id: "preserve-purchase-output",
        children: "Preserve purchase output"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, ", _jsx(_components.code, {
          children: "Order Completed"
        }), " and ", _jsx(_components.code, {
          children: "Completed Order"
        }), " are purchases. The plugin logs one purchase per product, using ", _jsx(_components.code, {
          children: "sku"
        }), ", then ", _jsx(_components.code, {
          children: "product_id"
        }), ", then ", _jsx(_components.code, {
          children: "name"
        }), " as the product ID. Other event names remain custom events unless you configure purchase detection."]
      }), "\n", _jsxs(_components.p, {
        children: ["Supply a finite ", _jsx(_components.code, {
          children: "price"
        }), " for each product, including ", _jsx(_components.code, {
          children: "0"
        }), " for free items. Quantity defaults to ", _jsx(_components.code, {
          children: "1"
        }), " when omitted and must be an integer from ", _jsx(_components.code, {
          children: "1"
        }), " to ", _jsx(_components.code, {
          children: "100"
        }), " when supplied. Send ", _jsx(_components.code, {
          children: "currency"
        }), " as a three-letter code, or configure ", _jsx(_components.code, {
          children: "defaultCurrency"
        }), " as a fallback for missing or invalid currency. The plugin trims whitespace and uppercases currency codes, with no automatic USD default. Purchases with invalid inputs are rejected."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.code, {
          children: "purchaseDetection: [\"Your Purchase Event\"]"
        }), " on browser and React Native, ", _jsx(_components.code, {
          children: "purchaseDetection: .eventNames(...)"
        }), " on iOS, or ", _jsx(_components.code, {
          children: ".purchaseDetection(PurchaseDetection.eventNames(...))"
        }), " on Android for another purchase event name."]
      }), "\n", _jsxs(_components.p, {
        children: ["The examples below map this Hightouch ", _jsx(_components.code, {
          children: "Order Completed"
        }), " payload. They cover the fields shown, including one order-level custom attribute (", _jsx(_components.code, {
          children: "channel"
        }), ") and one product-level custom attribute (", _jsx(_components.code, {
          children: "color"
        }), "). Extend the mappings for your own fields, coupons, promotions, or impressions before cutover."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"order_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"order-123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"revenue\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "26"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"tax\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "3"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"shipping\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "3"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"currency\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"USD\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"channel\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"app\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"products\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sku\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sku-1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"T-shirt\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"category\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Clothing\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "13"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"quantity\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"color\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"blue\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Purchase hooks receive the mapped purchase and its original order and product. They change the Braze purchase without changing the event collected by Hightouch. A hook can return no purchase to skip it. The mobile examples below use numeric ", _jsx(_components.code, {
          children: "price"
        }), " and, when supplied, numeric ", _jsx(_components.code, {
          children: "quantity"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "browser-purchases",
        children: "Browser purchases"
      }), "\n", _jsxs(_components.p, {
        children: ["The mParticle browser kit sends product custom attributes plus ", _jsx(_components.code, {
          children: "Sku"
        }), ". It does not automatically include the order's ", _jsx(_components.code, {
          children: "Transaction Id"
        }), " or totals. Its default product ID is the product name."]
      }), "\n", _jsxs(_components.p, {
        children: ["Add these options to the ", _jsx(_components.code, {
          children: "destinations.Braze"
        }), " setup on the ", _jsx(_components.a, {
          href: "/events/sdks/browser#braze",
          children: "browser SDK page"
        }), ". This example preserves ", _jsx(_components.code, {
          children: "color"
        }), " as a custom product attribute:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " purchaseOptions = {\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "purchaseGrouping"
          }), ": { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mode"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"perProduct\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "identifier"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), " },\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "transformPurchase"
          }), ": ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "purchase, { product }"
            }), ") =>"]
          }), " {\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (!product) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ";\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " {\n      ...purchase,\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "color"
          }), ": product.", _jsx(_components.span, {
            className: "hljs-property",
            children: "color"
          }), ", ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Sku"
          }), ": product.", _jsx(_components.span, {
            className: "hljs-property",
            children: "sku"
          }), " },\n    };\n  },\n};\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// Include ...purchaseOptions inside destinations.Braze."
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "If you previously added transaction fields as custom product attributes, include those explicitly. Keep purchases per product to match the browser kit; mobile's bundled commerce recipe does not describe the browser kit's behavior."
      }), "\n", _jsx(_components.h3, {
        id: "ios-purchases",
        children: "iOS purchases"
      }), "\n", _jsxs(_components.p, {
        children: ["The iOS kit includes renamed transaction fields, flattened custom attributes, and string-valued product fields, including ", _jsx(_components.code, {
          children: "Total Product Amount"
        }), ". This helper preserves that output for the sample payload. It also supports the bundled option described below:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-swift",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " Foundation\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " HightouchBraze\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "legacyProperties"
          }), "(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "_"
          }), " ", _jsx(_components.span, {
            className: "hljs-params",
            children: "order"
          }), ": [", _jsx(_components.span, {
            className: "hljs-params",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "], ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "_"
          }), " ", _jsx(_components.span, {
            className: "hljs-params",
            children: "product"
          }), ": [", _jsx(_components.span, {
            className: "hljs-params",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "]", _jsx(_components.span, {
            className: "hljs-operator",
            children: "?"
          }), ",\n                      ", _jsx(_components.span, {
            className: "hljs-params",
            children: "bundled"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "Bool"
          }), ") -> [", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "] {\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " orderNames ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"order_id\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Transaction Id\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"revenue\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Total Amount\""
          }), ",\n                      ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"tax\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Tax Amount\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"shipping\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Shipping Amount\""
          }), "]\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " result ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " [", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "]()\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " (key, name) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " orderNames { result[name] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " order[key] }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " attributes ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " [", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "]()\n    attributes[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"channel\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " order[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"channel\""
          }), "]\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " bundled {\n        result[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Attributes\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " attributes\n    } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " {\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " (key, value) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " attributes { result[key] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), "(describing: value) }\n    }\n\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "mapProduct"
          }), "(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "_"
          }), " ", _jsx(_components.span, {
            className: "hljs-params",
            children: "product"
          }), ": [", _jsx(_components.span, {
            className: "hljs-params",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "]) -> [", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "] {\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " item ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " product\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "=="
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "nil"
          }), " { item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " mapped ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " [", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "]()\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " names ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " [", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Name\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Category\""
          }), "]\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " bundled {\n            names.merge([", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sku\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Id\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"price\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Item Price\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Quantity\""
          }), "]) { ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "_"
          }), ", new ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " new }\n        }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " (key, name) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " names {\n            ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " value ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " item[key] { mapped[name] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), "(describing: value) }\n        }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " price ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"price\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as?"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "NSNumber"
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " quantity ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as?"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "NSNumber"
          }), " {\n            mapped[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Total Product Amount\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "NSNumber"
          }), "(value: price.doubleValue ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " quantity.doubleValue).stringValue\n        }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " bundled {\n            ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " custom ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " [", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "]()\n            custom[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"color\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"color\""
          }), "]\n            mapped[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Attributes\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " custom\n        } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " color ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"color\""
          }), "] {\n            mapped[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"color\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), "(describing: color)\n        }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " mapped\n    }\n\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " bundled {\n        result[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"products\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " (order[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"products\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as?"
          }), " [[String: ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Any"
          }), "]] ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "??"
          }), " []).map(mapProduct)\n    } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " product ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " product {\n        result.merge(mapProduct(product)) { ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "_"
          }), ", value ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " value }\n    }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " result\n}\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " options ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "BrazeDestination"
          }), ".", _jsx(_components.span, {
            className: "hljs-type",
            children: "Options"
          }), "(transformPurchase: { purchase, context ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "guard"
          }), " context.product ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "nil"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " { ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "nil"
          }), " }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " result ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " purchase\n    result.properties ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " legacyProperties(context.order, context.product, bundled: ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), ")\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " result\n})\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// Pass options to BrazeDestination(apiKey:endpoint:options:)."
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "android-purchases",
        children: "Android purchases"
      }), "\n", _jsxs(_components.p, {
        children: ["The Android kit includes renamed order and product fields and flattened custom attributes. Unlike iOS, its per-product purchase properties do not include ", _jsx(_components.code, {
          children: "Total Product Amount"
        }), ". Its bundled product fields use numbers for price, quantity, and total."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use this helper and transformer with the ", _jsx(_components.a, {
          href: "/events/sdks/android#braze",
          children: "Android setup"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-kotlin",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " com.hightouch.analytics.braze.BrazeIntegration\n\n", _jsxs(_components.span, {
            className: "hljs-function",
            children: [_jsx(_components.span, {
              className: "hljs-keyword",
              children: "fun"
            }), " ", _jsx(_components.span, {
              className: "hljs-title",
              children: "legacyProperties"
            }), _jsxs(_components.span, {
              className: "hljs-params",
              children: ["(order: ", _jsx(_components.span, {
                className: "hljs-type",
                children: "Map"
              }), "<", _jsx(_components.span, {
                className: "hljs-type",
                children: "String"
              }), ", Any>, product: ", _jsx(_components.span, {
                className: "hljs-type",
                children: "Map"
              }), "<", _jsx(_components.span, {
                className: "hljs-type",
                children: "String"
              }), ", Any>?,\n                     bundled: ", _jsx(_components.span, {
                className: "hljs-type",
                children: "Boolean"
              }), ")"]
            })]
          }), ": Map<String, Any> {\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " result = mutableMapOf<String, Any>()\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " orderNames = mapOf(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"order_id\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Transaction Id\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"revenue\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Total Amount\""
          }), ",\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"tax\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Tax Amount\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"shipping\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Shipping Amount\""
          }), ")\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " ((key, name) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " orderNames) order[key]?.let { result[name] = it }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " attributes = order.filterKeys { it == ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"channel\""
          }), " }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled) result[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Attributes\""
          }), "] = attributes ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " result.putAll(attributes)\n\n    ", _jsxs(_components.span, {
            className: "hljs-function",
            children: [_jsx(_components.span, {
              className: "hljs-keyword",
              children: "fun"
            }), " ", _jsx(_components.span, {
              className: "hljs-title",
              children: "mapProduct"
            }), _jsxs(_components.span, {
              className: "hljs-params",
              children: ["(product: ", _jsx(_components.span, {
                className: "hljs-type",
                children: "Map"
              }), "<", _jsx(_components.span, {
                className: "hljs-type",
                children: "String"
              }), ", Any>)"]
            })]
          }), ": Map<String, Any> {\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " item = ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (product.containsKey(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), ")) product ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " product + (", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ")\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " mapped = mutableMapOf<String, Any>()\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " names = mutableMapOf(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Name\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Category\""
          }), ")\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled) names.putAll(mapOf(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sku\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Id\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"price\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Item Price\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), " to ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Quantity\""
          }), "))\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " ((key, name) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " names) item[key]?.let { mapped[name] = it }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " custom = item.filterKeys { it == ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"color\""
          }), " }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled) {\n            mapped[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Attributes\""
          }), "] = custom\n            ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " price = item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"price\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " Number\n            ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " quantity = item[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"quantity\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " Number\n            mapped[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Total Product Amount\""
          }), "] = price.toDouble() * quantity.toDouble()\n        } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " {\n            mapped.putAll(custom)\n        }\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " mapped\n    }\n\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled) {\n        ", _jsxs(_components.span, {
            className: "hljs-meta",
            children: ["@Suppress(", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"UNCHECKED_CAST\""
            }), ")"]
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " products = order[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"products\""
          }), "] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " List<Map<String, Any>>\n        result[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"products\""
          }), "] = products.map(::mapProduct)\n    } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (product != ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ") {\n        result.putAll(mapProduct(product))\n    }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " result\n}\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " factory = BrazeIntegration.builder(context, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_BRAZE_SDK_API_KEY\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_BRAZE_SDK_ENDPOINT\""
          }), ")\n    .purchaseTransformer { purchase, input ->\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (input.product() == ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ") ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " purchase.withProperties(legacyProperties(input.order(), input.product(), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "))\n    }\n    .build()\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// Pass factory to Analytics.Builder.use(...)."
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "react-native-purchases",
        children: "React Native purchases"
      }), "\n", _jsx(_components.p, {
        children: "Match the native kit used on each operating system. This helper preserves the iOS and Android differences described above for the sample payload:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-jsx",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "BrazePlugin"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"@ht-sdks/events-sdk-react-native-plugin-braze\""
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Platform"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"react-native\""
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "legacyProperties"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "order, product, bundled, ios"
          }), ") {\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " result = {};\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " orderNames = {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "order_id"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Transaction Id\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "revenue"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Total Amount\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "tax"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Tax Amount\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "shipping"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Shipping Amount\""
          }), ",\n  };\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " [key, name] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "entries"
          }), "(orderNames)) {\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (order[key] !== ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "undefined"
          }), ") result[name] = order[key];\n  }\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " attributes =\n    order.", _jsx(_components.span, {
            className: "hljs-property",
            children: "channel"
          }), " === ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "undefined"
          }), " ? {} : { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "channel"
          }), ": order.", _jsx(_components.span, {
            className: "hljs-property",
            children: "channel"
          }), " };\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled) result.", _jsx(_components.span, {
            className: "hljs-property",
            children: "Attributes"
          }), " = attributes;\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "assign"
          }), "(\n      result,\n      ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "fromEntries"
          }), "(\n        ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "entries"
          }), "(attributes).", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "map"
          }), "(", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "[key, value]"
            }), ") =>"]
          }), " [\n          key,\n          ios ? ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "String"
          }), "(value) : value,\n        ])\n      )\n    );\n\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "mapProduct"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "product"
          }), ") {\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " item = {\n      ...product,\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "quantity"
          }), ": product.", _jsx(_components.span, {
            className: "hljs-property",
            children: "quantity"
          }), " === ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "undefined"
          }), " ? ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " : product.", _jsx(_components.span, {
            className: "hljs-property",
            children: "quantity"
          }), ",\n    };\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " mapped = {};\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " names = { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Name\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Category\""
          }), " };\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled)\n      ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "assign"
          }), "(names, {\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sku"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Id\""
          }), ",\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "price"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Item Price\""
          }), ",\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "quantity"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Quantity\""
          }), ",\n      });\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " [key, name] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "entries"
          }), "(names)) {\n      ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (item[key] !== ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "undefined"
          }), ")\n        mapped[name] = ios ? ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "String"
          }), "(item[key]) : item[key];\n    }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " custom = item.", _jsx(_components.span, {
            className: "hljs-property",
            children: "color"
          }), " === ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "undefined"
          }), " ? {} : { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "color"
          }), ": item.", _jsx(_components.span, {
            className: "hljs-property",
            children: "color"
          }), " };\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled) mapped.", _jsx(_components.span, {
            className: "hljs-property",
            children: "Attributes"
          }), " = custom;\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "assign"
          }), "(\n        mapped,\n        ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "fromEntries"
          }), "(\n          ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "entries"
          }), "(custom).", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "map"
          }), "(", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "[key, value]"
            }), ") =>"]
          }), " [\n            key,\n            ios ? ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "String"
          }), "(value) : value,\n          ])\n        )\n      );\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (ios || bundled) {\n      ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " total = item.", _jsx(_components.span, {
            className: "hljs-property",
            children: "price"
          }), " * item.", _jsx(_components.span, {
            className: "hljs-property",
            children: "quantity"
          }), ";\n      mapped[", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Total Product Amount\""
          }), "] = ios ? ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "String"
          }), "(total) : total;\n    }\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " mapped;\n  }\n\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " (bundled) result.", _jsx(_components.span, {
            className: "hljs-property",
            children: "products"
          }), " = order.", _jsx(_components.span, {
            className: "hljs-property",
            children: "products"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "map"
          }), "(mapProduct);\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Object"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "assign"
          }), "(result, ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "mapProduct"
          }), "(product));\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " result;\n}\n\nhtClient.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "add"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "plugin"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "BrazePlugin"
          }), "({\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "transformPurchase"
          }), ": ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "purchase, { order, product }"
            }), ") =>"]
          }), "\n      product\n        ? {\n            ...purchase,\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "legacyProperties"
          }), "(\n              order,\n              product,\n              ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), ",\n              ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Platform"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "OS"
          }), " === ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ios\""
          }), "\n            ),\n          }\n        : ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ",\n  }),\n});\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This example uses JavaScript-compatible function bodies. Add types for your tracking plan if you use TypeScript. Keep ", _jsx(_components.code, {
          children: "defaultSettings.integrations.Appboy"
        }), " configured as shown on the SDK page."]
      }), "\n", _jsx(_components.h3, {
        id: "preserve-bundled-mobile-purchases",
        children: "Preserve bundled mobile purchases"
      }), "\n", _jsxs(_components.p, {
        children: ["If ", _jsx(_components.code, {
          children: "bundleCommerceEventData"
        }), " was enabled, log one purchase per order with product ID ", _jsx(_components.code, {
          children: "eCommerce - purchase"
        }), ", order revenue as its price, and quantity ", _jsx(_components.code, {
          children: "1"
        }), ". Place custom order and product attributes under ", _jsx(_components.code, {
          children: "Attributes"
        }), ". Use the platform helper above to preserve product field names and types."]
      }), "\n", _jsx(_components.p, {
        children: "On iOS, replace the per-product options with:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-swift",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " options ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "BrazeDestination"
          }), ".", _jsx(_components.span, {
            className: "hljs-type",
            children: "Options"
          }), "(\n    purchaseGrouping: .perOrder,\n    transformPurchase: { purchase, context ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "var"
          }), " result ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " purchase\n        result.productId ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"eCommerce - purchase\""
          }), "\n        result.quantity ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "\n        result.properties ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " legacyProperties(context.order, ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "nil"
          }), ", bundled: ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), ")\n        ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " result\n    }\n)\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "On Android, replace the per-product builder configuration with:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-kotlin",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " com.hightouch.analytics.braze.PurchaseGrouping\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "val"
          }), " factory = BrazeIntegration.builder(context, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_BRAZE_SDK_API_KEY\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_BRAZE_SDK_ENDPOINT\""
          }), ")\n    .purchaseGrouping(PurchaseGrouping.perOrder())\n    .purchaseTransformer { purchase, input ->\n        purchase.withProductId(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"eCommerce - purchase\""
          }), ")\n            .withQuantity(", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ")\n            .withProperties(legacyProperties(input.order(), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ", ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), "))\n    }\n    .build()\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "On React Native, replace the per-product plugin registration with:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-jsx",
          children: ["htClient.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "add"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "plugin"
          }), ": ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "BrazePlugin"
          }), "({\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "purchaseGrouping"
          }), ": { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mode"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"perOrder\""
          }), " },\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "transformPurchase"
          }), ": ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "purchase, { order }"
            }), ") =>"]
          }), " ({\n      ...purchase,\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "productId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"eCommerce - purchase\""
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "quantity"
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "legacyProperties"
          }), "(\n        order,\n        ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "undefined"
          }), ",\n        ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), ",\n        ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Platform"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "OS"
          }), " === ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ios\""
          }), "\n      ),\n    }),\n  }),\n});\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Do not combine per-order grouping with a per-product identifier option. Supply the original order revenue explicitly. Compare tax, shipping, discounts, and quantities against your old output instead of recomputing revenue from products."
      }), "\n", _jsx(_components.h2, {
        id: "preserve-messaging-and-application-behavior",
        children: "Preserve messaging and application behavior"
      }), "\n", _jsx(_components.p, {
        children: "Keep Braze's UI, Content Card subscriptions, push registration, and permission handling when removing the mParticle kit. Transfer any kit-owned setup to your app before removing the kit. Use the SDK examples linked from the setup pages for application wiring."
      }), "\n", _jsx(_components.p, {
        children: "If your app supplies an already initialized Braze instance, keep its session lifecycle handling. The Android plugin manages sessions when it initializes Braze itself. Avoid registering another session lifecycle handler for that setup."
      }), "\n", _jsx(_components.p, {
        children: "The plugin does not copy mParticle's event-to-user attribute rules, subscription-group mappings, consent mappings, or web push primer. Implement required rules explicitly in your application using Hightouch traits or the relevant Braze APIs. Sending a similarly named custom trait does not configure a Braze subscription group or consent API."
      }), "\n", _jsx(_components.h2, {
        id: "validate-and-cut-over",
        children: "Validate and cut over"
      }), "\n", _jsx(_components.p, {
        children: "Compare the old and new builds with equivalent test users and the same kit settings:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Confirm external user IDs match exactly and anonymous-to-known transitions behave as expected."
        }), "\n", _jsxs(_components.li, {
          children: ["Compare standard fields, custom attributes, and their value types. Repeat an unchanged ", _jsx(_components.code, {
            children: "identify"
          }), " and confirm the plugin avoids redundant attribute updates."]
        }), "\n", _jsx(_components.li, {
          children: "Compare custom event names, page/screen names, and properties. Include any expanded commerce events your campaigns use."
        }), "\n", _jsx(_components.li, {
          children: "Test multiple products, SKU and name identifiers, custom fields, and bundled orders. Compare purchase counts, prices, quantities, currency, property names, and types."
        }), "\n", _jsx(_components.li, {
          children: "Trigger an in-app message, load Content Cards, and test push behavior using your application's presentation code."
        }), "\n", _jsxs(_components.li, {
          children: ["Verify ", _jsx(_components.code, {
            children: "reset()"
          }), " and a later ", _jsx(_components.code, {
            children: "identify"
          }), " with another user ID. Do not expect ", _jsx(_components.code, {
            children: "reset()"
          }), " to make the Braze user anonymous."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "After validation, enable the replacement for a limited rollout and confirm each production event reaches Braze once. Disable the old kit and overlapping direct calls or server-side forwarding for those events. Keep the underlying Braze SDK and required application setup when removing mParticle."
      }), "\n", _jsxs(_components.p, {
        children: ["These recipes follow the ", _jsx(_components.a, {
          href: "https://github.com/mparticle-integrations/mparticle-javascript-integration-appboy",
          children: "browser kit"
        }), ", ", _jsx(_components.a, {
          href: "https://github.com/mparticle-integrations/mparticle-apple-integration-appboy",
          children: "iOS kit"
        }), ", and ", _jsx(_components.a, {
          href: "https://github.com/mparticle-integrations/mparticle-android-integration-appboy",
          children: "Android kit"
        }), ". Compare your installed kit version and custom mappings before applying them."]
      })]
    });
  }
}
export default MDXContent;
